import { RouteObject } from "react-router-dom";
import { Navigate, useParams } from "react-router-dom";
import { ProtectedRoute } from "src/authentication/components/ProtectedRoute";
import {
    IconBell,
    IconCalendarCog,
    IconHome,
    IconId,
    IconIdBadge2,
    IconMessage2Question,
    IconProgressCheck,
    IconQrcode,
    IconSchool,
    IconShieldCheck,
    IconUserCircle,
} from "@tabler/icons-react";
import {
    MY_CHILD_PROTECTION_PATH,
    MY_TRAINING_PATH,
    myClearancePath,
} from "src/child-protection/clearances/mine/paths";
import { MY_DETAILS_PATH } from "src/profile/my-account/paths";

const RedirectToMinistrySeasonRegistration = () => {
    const { ministrySeasonId } = useParams<{ ministrySeasonId: string }>();
    return <Navigate to={`/public/forms/register-for-ministry-season/${ministrySeasonId}`} replace />;
};

const RedirectToEventBooking = () => {
    const { eventId } = useParams<{ eventId: string }>();
    return <Navigate to={`/public/forms/event/${eventId}`} replace />;
};

/** PUB45: the old page is a sheet over Forms & bookings now, so a saved link still opens it */
const RedirectToUnlinkedRegistration = () => {
    const { unlinkedRegistrationId } = useParams<{ unlinkedRegistrationId: string }>();
    return <Navigate to={`/public/forms?registration=${unlinkedRegistrationId}`} replace />;
};

const RedirectToMyClearance = () => {
    const { clearanceId, "*": rest } = useParams<{ clearanceId: string; "*": string }>();
    return <Navigate to={[myClearancePath(clearanceId ?? ""), rest].filter(Boolean).join("/")} replace />;
};

export const publicRoutes: RouteObject[] = [
    {
        element: <ProtectedRoute publicPermitted />,

        children: [
            {
                index: true,
                lazy: () => import("./authenticated-index/AuthenticatedPublicIndex"),
                handle: { crumb: "Home", icon: IconHome },
            },

            {
                path: "account",
                handle: { crumb: "My account", icon: IconUserCircle },

                children: [
                    { index: true, lazy: () => import("src/profile/my-account/MyAccountScreen") },

                    {
                        path: "details",
                        lazy: () => import("src/profile/my-details/MyDetailsScreen"),
                        handle: { crumb: "My details", icon: IconId },
                    },

                    {
                        path: "notifications",
                        lazy: () => import("src/profile/my-account/NotificationsAndRemindersScreen"),
                        handle: { crumb: "Notifications & reminders", icon: IconBell },
                    },

                    {
                        path: "phone-calendar",
                        lazy: () => import("src/profile/my-account/MyAccountPhoneCalendarScreen"),
                        handle: { crumb: "Phone calendar", icon: IconCalendarCog },
                    },
                ],
            },

            // The address before `[SELF32]`, kept for links already saved or sent
            { path: "profile", element: <Navigate to={MY_DETAILS_PATH} replace /> },

            {
                path: "check-in-passes",
                lazy: () => import("src/calendar-events/check-in-passes/mine/MyCheckInPassesScreen"),
                handle: { crumb: "Check-in passes", icon: IconQrcode },
            },

            /**
             * Not under `/child-protection`, because applicants and referees often hold no
             * role and `ProtectedRoute` sends those to the index unless the branch is
             * `publicPermitted`. The server narrows what they may read.
             */
            {
                path: "my-child-protection",
                handle: { crumb: "My child protection & training", icon: IconShieldCheck },

                children: [
                    {
                        index: true,
                        lazy: () => import("src/child-protection/clearances/mine/MyChildProtectionScreen"),
                    },

                    {
                        path: "checks/:clearanceId",
                        handle: { crumb: "Check", icon: IconIdBadge2 },

                        children: [
                            {
                                index: true,
                                lazy: () => import("src/child-protection/clearances/mine/MyClearanceScreen"),
                            },

                            {
                                path: "preliminary-application",
                                handle: { crumb: "Agree to checks" },
                                lazy: () =>
                                    import("src/child-protection/clearances/mine/preliminary-application/MyPreliminaryApplicationScreen"),

                                children: [
                                    { index: true, element: <Navigate to="your-details" replace /> },
                                    {
                                        path: "your-details",
                                        lazy: () =>
                                            import("src/child-protection/clearances/mine/preliminary-application/steps/MyPreliminaryApplicationDetailsStep"),
                                    },
                                    {
                                        path: "the-work",
                                        lazy: () =>
                                            import("src/child-protection/clearances/mine/preliminary-application/steps/MyPreliminaryApplicationWorkStep"),
                                    },
                                    {
                                        path: "permission",
                                        lazy: () =>
                                            import("src/child-protection/clearances/mine/preliminary-application/steps/MyPreliminaryApplicationPermissionStep"),
                                    },
                                ],
                            },

                            {
                                path: "application-form",
                                handle: { crumb: "Application form" },
                                lazy: () =>
                                    import("src/child-protection/clearances/mine/application-form/MyApplicationFormScreen"),

                                children: [
                                    { index: true, element: <Navigate to="your-details" replace /> },
                                    {
                                        path: "your-details",
                                        lazy: () =>
                                            import("src/child-protection/clearances/mine/application-form/steps/MyApplicationFormDetailsStep"),
                                    },
                                    {
                                        path: "your-health",
                                        lazy: () =>
                                            import("src/child-protection/clearances/mine/application-form/steps/MyApplicationFormHealthStep"),
                                    },
                                    {
                                        path: "your-referees",
                                        lazy: () =>
                                            import("src/child-protection/clearances/mine/application-form/steps/MyApplicationFormRefereesStep"),
                                    },
                                    {
                                        path: "sign",
                                        lazy: () =>
                                            import("src/child-protection/clearances/mine/application-form/steps/MyApplicationFormSignStep"),
                                    },
                                ],
                            },
                        ],
                    },

                    {
                        path: "training",
                        lazy: () => import("src/child-protection/clearances/mine/MyTrainingScreen"),
                        handle: { crumb: "Training", icon: IconSchool },
                    },
                ],
            },

            // The addresses before `[CLR60D]`, kept for links already saved or sent
            { path: "my-child-protection-check", element: <Navigate to={MY_CHILD_PROTECTION_PATH} replace /> },
            { path: "my-child-protection-check/:clearanceId/*", element: <RedirectToMyClearance /> },
            { path: "my-child-protection-training", element: <Navigate to={MY_TRAINING_PATH} replace /> },

            {
                path: "references",
                handle: { crumb: "References asked of you", icon: IconMessage2Question },

                children: [
                    {
                        index: true,
                        lazy: () => import("src/child-protection/clearances/referee/MyReferencesScreen"),
                    },
                    {
                        path: ":referenceId",
                        lazy: () => import("src/child-protection/clearances/referee/GiveReferenceScreen"),
                    },
                ],
            },

            // New merged forms & bookings route
            {
                path: "forms",
                lazy: () => import("./event-bookings/PublicEventBookingsOutlet"),
                handle: { crumb: "Forms & bookings", icon: IconProgressCheck },

                children: [
                    {
                        index: true,
                        lazy: () => import("./forms/PublicFormsScreen"),
                    },

                    {
                        path: "event/:eventId",
                        lazy: () => import("./event-bookings/booking-form/PublicEventBookingScreen"),
                    },

                    {
                        path: "give-parental-consent",

                        children: [
                            {
                                index: true,
                                lazy: () =>
                                    import("./consent-and-medical/give-parental-consent/PublicGiveParentalConsentInformation"),
                            },

                            {
                                lazy: () =>
                                    import("./consent-and-medical/give-parental-consent/PublicGiveParentalConsentScreen"),
                                children: [
                                    {
                                        path: "about-your-child",
                                        lazy: () =>
                                            import("./consent-and-medical/give-parental-consent/steps/PublicGiveParentalConsentFormAboutYourChildStep"),
                                    },
                                    {
                                        path: "your-details",
                                        lazy: () =>
                                            import("./consent-and-medical/give-parental-consent/steps/PublicGiveParentalConsentFormYourDetailsStep"),
                                    },
                                    {
                                        path: "consent",
                                        lazy: () =>
                                            import("./consent-and-medical/give-parental-consent/steps/PublicGiveParentalConsentFormConsentStep"),
                                    },
                                    {
                                        path: "declaration",
                                        lazy: () =>
                                            import("./consent-and-medical/give-parental-consent/steps/PublicGiveParentalConsentFormDeclarationStep"),
                                    },
                                ],
                            },
                        ],
                    },

                    {
                        path: "view-parental-consent/:consentFormId",
                        lazy: () =>
                            import("./consent-and-medical/view-parental-consent/PublicViewParentalConsentFormScreen"),
                    },

                    {
                        path: "register-for-ministry-season/:ministrySeasonId",
                        lazy: () =>
                            import("./consent-and-medical/register-for-ministry-season/PublicMinistrySeasonRegistrationScreen"),
                    },

                    {
                        path: "unlinked-registration/:unlinkedRegistrationId",
                        element: <RedirectToUnlinkedRegistration />,
                    },
                ],
            },

            // Legacy redirects — keep old URLs working
            {
                path: "event-bookings",
                children: [
                    {
                        index: true,
                        element: <Navigate to="/public/forms" replace />,
                    },
                    {
                        path: "event/:eventId",
                        element: <RedirectToEventBooking />,
                    },
                ],
            },

            {
                path: "consent-and-medical",
                children: [
                    {
                        index: true,
                        element: <Navigate to="/public/forms" replace />,
                    },
                    {
                        path: "give-parental-consent",
                        element: <Navigate to="/public/forms/give-parental-consent" replace />,
                    },
                    {
                        path: "register-for-ministry-season/:ministrySeasonId",
                        element: <RedirectToMinistrySeasonRegistration />,
                    },
                ],
            },
        ],
    },
];
